/**
 * ブロックコンポーネント(src/components/blocks/**)に直書きされていた固定文言の JA/EN 辞書。
 * Codex EN リリース検査(2026-09-17)で out/en 全頁に JA が残存した是正。各ブロックは getLang(pathname) で引く。
 * JA 表示は不変(ja 側は従来の文字列そのまま)。
 */
import type { Lang } from '@/lib/lang'

export const blocksUi = {
  ja: {
    more: '詳しく見る', yen: '円(税込)', yenFrom: '円〜(税込)', provisional: '価格は仮', provisionalPlan: '・仮価格', qty: '数量', preparing: '準備中',
    crestAlt: "Queen's Butler のマーク（G04 筆の環）", toc: '目次', includes: '料金に含まれるもの', excludes: '含まれないもの', staffButler: '担当執事',
    goodsEmpty: '商品のご案内は、ただいま執事が整えております。整い次第、こちらでご覧いただけます。',
    goodsNoMatch: '条件に合う商品は、ただいま執事が整えております。整い次第、こちらでご覧いただけます。',
    all: 'すべて', sort: '並び替え', sortNew: '新着順', sortAsc: '価格が安い順', sortDesc: '価格が高い順',
    roseAlt: '食卓を飾る薔薇', toSuffix: 'へ',
  },
  en: {
    more: 'Read more', yen: ' (incl. tax)', yenFrom: 'from (incl. tax)', provisional: 'Provisional price', provisionalPlan: ' · provisional', qty: 'Quantity', preparing: 'Coming soon',
    crestAlt: "Queen's Butler mark", toc: 'Contents', includes: 'Included in the price', excludes: 'Not included', staffButler: 'Your butler',
    goodsEmpty: 'Our butlers are preparing the items. They will appear here as soon as they are ready.',
    goodsNoMatch: 'No items match yet. Our butlers are preparing them; they will appear here as soon as they are ready.',
    all: 'All', sort: 'Sort', sortNew: 'Newest', sortAsc: 'Price: low to high', sortDesc: 'Price: high to low',
    roseAlt: 'Roses adorning the table', toSuffix: '',
  },
} as const

export const blocksUiFor = (lang: Lang) => blocksUi[lang]

/** Services ブロック(D-1a/D-1b)のカード文言 */
export const servicesEn: Record<'salon' | 'studio' | 'footman' | 'apprentice' | 'shop', { alt: string; ja: string; text: string }> = {
  salon: { alt: 'A female butler pouring tea in the dining room', ja: 'The Salon', text: 'A manor for ladies, with a butler in attendance. By appointment only, one party at a time.' },
  studio: { alt: 'A portrait taken in the candlelit dining room', ja: 'The Studio', text: 'From photography to meetings, green rooms and location shoots: an antique manor, with a butler.' },
  footman: { alt: 'A butler straightening the collar of a morning coat', ja: 'Apprentice Butler', text: 'Three hours in a real morning coat, learning the ways of a butler.' },
  apprentice: { alt: 'A butler straightening the collar of a morning coat', ja: 'Apprentice Butler', text: 'Three hours in a real morning coat, learning the ways of a butler.' },
  shop: { alt: 'A gilded plate and a single rose', ja: 'Online Shop', text: 'Everyday pieces bearing the butler’s crest and calligraphy.' },
}

/** BookIntro(トップの監修書籍紹介) */
export const bookIntroEn = {
  coverAlt: 'Cover of "Butler Manners & Culture Encyclopaedia" (Gakken), supervised by the Japan Butler Association',
  coverLabel: 'See our introduction to "Butler Manners & Culture Encyclopaedia" (Gakken, supervised by the Japan Butler Association)',
  heading: 'A book supervised by the Japan Butler Association',
  title: '"Butler Manners & Culture Encyclopaedia"',
  subtitle: 'The Beautiful Life of the English Gentleman — 50 Topics',
  lead1: 'Fifty topics on a butler’s attire and bearing, the manners of service and escort, and the culture of the English gentleman.',
  lead2: 'The Japan Butler Association, which runs Queen’s Butler, supervised the book. The attire our butlers wear and the manner in which they serve follow the etiquette set out in these pages.',
  meta: [['Supervised by', 'Japan Butler Association'], ['With', 'Butler café Swallowtail'], ['Publisher', 'Gakken'], ['Published', '17 September 2026'], ['Format', '144 pages · ¥1,870 (incl. tax)']] as Array<[string, string]>,
  more: 'About this book',
}

/** Collection ブロックの調度名(EN は collection.json の en を使う)。写真 alt は EN 側で「<en> — the manor's own furnishings」 */
export const collectionZoneEn: Record<string, string> = { dining: 'Dining Room', study: 'Study', lounge: 'Fireplace Room', bedroom: 'Bedroom', wardrobe: 'Wardrobe', tableware: 'Tableware' }
